import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isSameDay } from "date-fns";
import { IError } from "itsf-ui-common";
import type { RegisterOptions } from "react-hook-form";

export interface INoInstallActivationForm {
    activationDate: Date | null;
}

interface IParams {
    error?: IError;
    filteredActivationDateList: Date[];
    firstAvailableDate: Date;
    isLoading: boolean;
}

export const useInstallationNoInstall = (props: IParams) => {
    const { error, filteredActivationDateList, firstAvailableDate, isLoading } = props;
    const { t } = useTranslation(["installation", "validation"]);

    const emptyAvailability = !isLoading && error === undefined && filteredActivationDateList.length === 0;

    const noActivationDatesError: IError = useMemo(
        () => ({
            errorCode: "NO_ACTIVATION_DATES",
            errorMessage: t("installation:noActivationDatesAvailable"),
        }),
        [t]
    );

    const displayError = error ?? (emptyAvailability ? noActivationDatesError : undefined);

    const defaultMinDate = filteredActivationDateList.length > 0 ? filteredActivationDateList[0] : firstAvailableDate;
    const defaultMaxDate =
        filteredActivationDateList.length > 0
            ? filteredActivationDateList[filteredActivationDateList.length - 1]
            : firstAvailableDate;

    const shouldDisableDate = useCallback(
        (date: Date | null) => {
            if (!date || isLoading) {
                return false;
            }

            if (filteredActivationDateList.length === 0) {
                return true;
            }

            return !filteredActivationDateList.some((availableDate) => isSameDay(availableDate, date));
        },
        [filteredActivationDateList, isLoading]
    );

    const activationDateRules: RegisterOptions = useMemo(
        () => ({
            required: t("validation:required"),
            validate: (value: Date | null) => {
                if (!value) {
                    return t("validation:required");
                }

                const isDateValid = filteredActivationDateList.some((d) => isSameDay(d, value));
                if (!isDateValid) {
                    return t("installation:pickValidActivationDate");
                }

                return true;
            },
        }),
        [filteredActivationDateList, t]
    );

    return {
        activationDateRules,
        defaultMaxDate,
        defaultMinDate,
        displayError,
        emptyAvailability,
        shouldDisableDate,
    };
};
